import { ROUTES, type DecisionResponse } from '@kkp-fraud/shared';
import SAMPLE from '../../../fixtures/sample-request.json';
import { FEEDER_FIELDS, type FeederField } from './feederFields';
import type { SourceId } from './sources';

/**
 * API Keys → API reference: the pure half. What the decision API's three public routes take and
 * answer, the example request (KKP's sample, `fixtures/sample-request.json`, imported at build
 * time — not a copy), and the code examples built from it in five languages.
 *
 * Every example carries {@link KEY_PLACEHOLDER}, never a key: the console holds a real key only in
 * the Try-it field, and nothing here ever reads that field.
 */

/** What every example puts where the key goes. */
export const KEY_PLACEHOLDER = 'YOUR_API_KEY';

export type EndpointId = 'predetect' | 'healthz' | 'readyz';

export interface Endpoint {
  id: EndpointId;
  method: 'POST' | 'GET';
  path: string;
  /** Needs `Authorization: <API key>` — only `/v1/*`. */
  auth: boolean;
}

export const ENDPOINTS: readonly Endpoint[] = [
  { id: 'predetect', method: 'POST', path: ROUTES.predetect, auth: true },
  { id: 'healthz', method: 'GET', path: ROUTES.healthz, auth: false },
  { id: 'readyz', method: 'GET', path: ROUTES.readyz, auth: false },
];

/** A request header of `/v1/predetect`; the description is `apiref.header.<id>.desc`. */
export interface HeaderDoc {
  id: 'authorization' | 'content_type' | 'request_id';
  name: string;
  value: string;
  required: boolean;
}

export const PREDETECT_HEADERS: readonly HeaderDoc[] = [
  { id: 'authorization', name: 'Authorization', value: KEY_PLACEHOLDER, required: true },
  { id: 'content_type', name: 'Content-Type', value: 'application/json', required: true },
  { id: 'request_id', name: 'x-request-id', value: '', required: false },
];

/** The two Transaction fields the engine refuses a request without (400 `validation_failed`). */
export const REQUIRED_FIELDS: ReadonlySet<string> = new Set(['reference_card_no', 'transaction_id']);

/** One Transaction field as the reference lists it: KKP's sheet row + the sample's value. */
export interface BodyField {
  key: string;
  /** KKP's own name for it (the sheet's, printed verbatim). */
  name: string;
  /** ISO 8583 data element, or null when KKP's host supplies the value. */
  de: string | null;
  /** Always `string` on the wire (KKP's sample sends every value as a JSON string). */
  type: 'string';
  required: boolean;
  sends: Record<SourceId, boolean>;
  /** The value in KKP's sample, or '' when the sample has none (then it is left out of the body). */
  sample: string;
}

const SAMPLE_TXN = SAMPLE.Transaction as Record<string, string>;

export const SAMPLE_CHANNEL_ID: string = SAMPLE.Channel.Id;

export const BODY_FIELDS: readonly BodyField[] = FEEDER_FIELDS.map((f: FeederField) => ({
  key: f.key,
  name: f.name,
  de: f.de,
  type: 'string' as const,
  required: REQUIRED_FIELDS.has(f.key),
  sends: f.sends,
  sample: SAMPLE_TXN[f.key] ?? '',
}));

/**
 * The response fields the reference documents — every key of the shared `DecisionResponse`
 * contract except `score` (being removed from the contract; not documented). A key added to or
 * removed from the contract fails `tsc` here until the reference follows. The description is
 * `apiref.resp.<key>`.
 */
export type DocumentedResponseKey = Exclude<keyof DecisionResponse, 'score'>;

export const RESPONSE_FIELDS: Readonly<Record<DocumentedResponseKey, { type: 'string' | 'number' | 'boolean' | 'array'; always: boolean }>> = {
  transaction_id: { type: 'string', always: true },
  decision: { type: 'string', always: true },
  reasons: { type: 'array', always: true },
  hits: { type: 'array', always: true },
  step_up_method: { type: 'string', always: true },
  response_code_hint: { type: 'string', always: true },
  engine_latency_ms: { type: 'number', always: true },
  service_latency_ms: { type: 'number', always: true },
  degraded: { type: 'boolean', always: false },
  engine_version: { type: 'string', always: true },
  schema_version: { type: 'string', always: true },
  decision_id: { type: 'string', always: true },
  alert: { type: 'boolean', always: false },
  skipped: { type: 'array', always: false },
  rule_sets: { type: 'array', always: false },
};

/** `/healthz` and `/readyz` — the keys an operator reads (`apiref.health.<key>` / `apiref.ready.<key>`). */
export const HEALTHZ_FIELDS = ['status', 'engineVersion', 'shadowMode'] as const;
export const READYZ_FIELDS = ['status', 'stateStoreOk', 'rulesLoaded', 'ruleSetVersion', 'ruleSets', 'apiClients', 'shadowMode'] as const;

/** The statuses `/v1/predetect` answers with (`apiref.status.<code>`). */
export const PREDETECT_STATUSES = ['200', '400', '401', '429', '500', '503'] as const;

// ---- the request -----------------------------------------------------------------------------

/** The editable example: the channel id and one value per Transaction field ('' = left out). */
export interface ExampleValues {
  channelId: string;
  transaction: Record<string, string>;
}

/** KKP's sample, with a fresh `transaction_id` (the sample's own is surely cached already). */
export function sampleValues(transactionId: string = newTransactionId()): ExampleValues {
  return {
    channelId: SAMPLE_CHANNEL_ID,
    transaction: { ...Object.fromEntries(BODY_FIELDS.map((f) => [f.key, f.sample])), transaction_id: transactionId },
  };
}

/**
 * A transaction id nobody has sent: `TRY-yyyyMMdd-HHmmss-xxxx` (UTC). Idempotency is keyed on
 * the transaction id, so a repeated id answers the FIRST decision again.
 */
export function newTransactionId(now: Date = new Date(), random: () => number = Math.random): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  const stamp = `${now.getUTCFullYear()}${p(now.getUTCMonth() + 1)}${p(now.getUTCDate())}-${p(now.getUTCHours())}${p(now.getUTCMinutes())}${p(now.getUTCSeconds())}`;
  const tail = Math.floor(random() * 0x10000).toString(16).toUpperCase().padStart(4, '0');
  return `TRY-${stamp}-${tail}`;
}

export interface PredetectBody {
  Channel?: { Id: string };
  Transaction: Record<string, string>;
}

/** The body the examples and Try-it send: fields in KKP's sheet order, an empty value left out. */
export function buildBody(values: ExampleValues): PredetectBody {
  const transaction: Record<string, string> = {};
  for (const f of BODY_FIELDS) {
    const v = values.transaction[f.key] ?? '';
    if (v !== '') transaction[f.key] = v;
  }
  return {
    ...(values.channelId !== '' ? { Channel: { Id: values.channelId } } : {}),
    Transaction: transaction,
  };
}

// ---- the code examples -----------------------------------------------------------------------

export type LanguageId = 'curl' | 'javascript' | 'python' | 'csharp' | 'postman';

/** The selector's order; each name is `apiref.lang.<id>`. */
export const LANGUAGES: readonly LanguageId[] = ['curl', 'javascript', 'python', 'csharp', 'postman'];

/** The public base without a trailing slash; the shell variable when the admin service did not say. */
export function apiBaseOf(base: string | null | undefined): string {
  return base ? base.replace(/\/+$/, '') : 'https://YOUR_DECISION_API';
}

const json = (value: unknown, indent = 2) => JSON.stringify(value, null, indent);

/** Indent every line after the first — to nest a multi-line literal inside code. */
const nest = (text: string, pad: string) => text.split('\n').join(`\n${pad}`);

/** A POSIX single-quoted shell word: `'` closes, an escaped quote, and reopens. */
const shellQuote = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

/** One endpoint's example in one language. `body` only for `/v1/predetect`. */
export function codeExample(lang: LanguageId, endpoint: Endpoint, base: string, body?: PredetectBody): string {
  const url = `${apiBaseOf(base)}${endpoint.path}`;
  const post = endpoint.method === 'POST';
  switch (lang) {
    case 'curl': {
      if (!post) return `curl -sS ${shellQuote(url)}`;
      return [
        `curl -sS -X POST ${shellQuote(url)} \\`,
        `  -H ${shellQuote(`Authorization: ${KEY_PLACEHOLDER}`)} \\`,
        `  -H 'Content-Type: application/json' \\`,
        `  --data-raw ${shellQuote(json(body))}`,
      ].join('\n');
    }
    case 'javascript': {
      if (!post) return [`const response = await fetch(${json(url)});`, 'console.log(response.status, await response.json());'].join('\n');
      return [
        `const response = await fetch(${json(url)}, {`,
        `  method: 'POST',`,
        `  headers: {`,
        `    Authorization: '${KEY_PLACEHOLDER}',`,
        `    'Content-Type': 'application/json',`,
        `  },`,
        `  body: JSON.stringify(${nest(json(body), '  ')}),`,
        `});`,
        'console.log(response.status, await response.json());',
      ].join('\n');
    }
    case 'python': {
      // Every value is a string, so the JSON literal is also a valid Python dict literal.
      if (!post) return ['import requests', '', `response = requests.get(${json(url)}, timeout=10)`, 'print(response.status_code, response.json())'].join('\n');
      return [
        'import requests',
        '',
        'response = requests.post(',
        `    ${json(url)},`,
        `    headers={"Authorization": "${KEY_PLACEHOLDER}"},`,
        `    json=${nest(json(body, 4), '    ')},`,
        '    timeout=10,',
        ')',
        'print(response.status_code, response.json())',
      ].join('\n');
    }
    case 'csharp': {
      const head = ['using System.Net.Http;', 'using System.Text;', '', 'using var http = new HttpClient();'];
      if (!post) {
        return [...head, `var response = await http.GetAsync(${json(url)});`, 'Console.WriteLine($"{(int)response.StatusCode} {await response.Content.ReadAsStringAsync()}");'].join('\n');
      }
      // A raw string literal: the JSON needs no escaping (a `"` inside a value is `\"` in JSON,
      // so three quotes in a row cannot occur).
      return [
        ...head,
        `using var request = new HttpRequestMessage(HttpMethod.Post, ${json(url)});`,
        `request.Headers.TryAddWithoutValidation("Authorization", "${KEY_PLACEHOLDER}");`,
        'request.Content = new StringContent("""',
        `    ${nest(json(body), '    ')}`,
        '    """, Encoding.UTF8, "application/json");',
        'using var response = await http.SendAsync(request);',
        'Console.WriteLine($"{(int)response.StatusCode} {await response.Content.ReadAsStringAsync()}");',
      ].join('\n');
    }
    case 'postman':
      return json(postmanCollection(endpoint, base, body));
  }
}

/** A Postman v2.1 collection with the one request; the key is the collection variable `apiKey`. */
export function postmanCollection(endpoint: Endpoint, base: string, body?: PredetectBody) {
  const post = endpoint.method === 'POST';
  return {
    info: {
      name: `KKP fraud decision API — ${endpoint.method} ${endpoint.path}`,
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    item: [
      {
        name: `${endpoint.method} ${endpoint.path}`,
        request: {
          method: endpoint.method,
          header: post
            ? [
                { key: 'Authorization', value: '{{apiKey}}' },
                { key: 'Content-Type', value: 'application/json' },
              ]
            : [],
          url: `{{baseUrl}}${endpoint.path}`,
          ...(post ? { body: { mode: 'raw', raw: json(body), options: { raw: { language: 'json' } } } } : {}),
        },
      },
    ],
    variable: [
      { key: 'baseUrl', value: apiBaseOf(base) },
      ...(post ? [{ key: 'apiKey', value: KEY_PLACEHOLDER }] : []),
    ],
  };
}

/** The Postman file's name. */
export const postmanFileName = (endpoint: Endpoint): string => `kkp-fraud-${endpoint.id}.postman_collection.json`;
